import { useState, type ElementType, type FC, type ReactNode } from "react";
import {
  Send,
  Upload,
  MessagesSquare,
  FileText,
  UserX,
  Settings,
  Smartphone,
  MessageSquare,
  LayoutDashboard,
  ChevronDown,
  ChevronRight,
  Search,
  Keyboard,
  Clock,
  AlertCircle,
} from "lucide-react";

type SectionId =
  | "quick-send"
  | "bulk-send"
  | "conversations"
  | "templates"
  | "opt-outs"
  | "settings"
  | "devices"
  | "messages"
  | "dashboard"
  | "faq";

interface Section {
  id: SectionId;
  icon: ElementType;
  title: string;
}

const sections: Section[] = [
  { id: "dashboard", icon: LayoutDashboard, title: "ダッシュボード" },
  { id: "quick-send", icon: Send, title: "SMS送信（個別）" },
  { id: "bulk-send", icon: Upload, title: "一斉送信" },
  { id: "conversations", icon: MessagesSquare, title: "会話" },
  { id: "templates", icon: FileText, title: "テンプレート" },
  { id: "messages", icon: MessageSquare, title: "メッセージ履歴" },
  { id: "opt-outs", icon: UserX, title: "配信停止" },
  { id: "devices", icon: Smartphone, title: "端末ステータス" },
  { id: "settings", icon: Settings, title: "設定" },
  { id: "faq", icon: AlertCircle, title: "よくある質問" },
];

function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="px-1.5 py-0.5 text-xs bg-slate-100 border border-slate-300 rounded font-mono">
      {children}
    </kbd>
  );
}

function StepNumber({ n }: { n: number }) {
  return (
    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex-shrink-0">
      {n}
    </span>
  );
}

function MockInput({ label, placeholder, width = "w-48" }: { label: string; placeholder: string; width?: string }) {
  return (
    <div className="flex-shrink-0">
      <span className="block text-xs text-slate-500 mb-1">{label}</span>
      <div className={`${width} px-3 py-2 border border-dashed border-slate-300 rounded-md text-sm text-slate-400 bg-slate-50`}>
        {placeholder}
      </div>
    </div>
  );
}

function MockButton({ children, color = "blue" }: { children: ReactNode; color?: string }) {
  const colors: Record<string, string> = {
    blue: "bg-blue-500 text-white",
    green: "bg-green-500 text-white",
    red: "bg-red-100 text-red-700",
    slate: "bg-slate-200 text-slate-600",
  };
  return (
    <span className={`inline-flex px-3 py-1.5 rounded-md text-xs font-medium ${colors[color] || colors.blue}`}>
      {children}
    </span>
  );
}

function MockBadge({ children, color = "green" }: { children: ReactNode; color?: string }) {
  const colors: Record<string, string> = {
    green: "bg-green-100 text-green-700",
    red: "bg-red-100 text-red-700",
    yellow: "bg-yellow-100 text-yellow-700",
    slate: "bg-slate-100 text-slate-600",
    blue: "bg-blue-100 text-blue-700",
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${colors[color] || colors.green}`}>
      {children}
    </span>
  );
}

function SegmentTable() {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      <table className="w-full text-sm">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">セグメント数</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">文字数</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">料金</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          <tr><td className="px-4 py-2">1通</td><td className="px-4 py-2">70文字以内</td><td className="px-4 py-2">3.3円</td></tr>
          <tr className="bg-slate-50"><td className="px-4 py-2">2通</td><td className="px-4 py-2">71〜134文字</td><td className="px-4 py-2">6.6円</td></tr>
          <tr><td className="px-4 py-2">3通</td><td className="px-4 py-2">135〜201文字</td><td className="px-4 py-2">9.9円</td></tr>
          <tr className="bg-slate-50"><td className="px-4 py-2">最大10通</td><td className="px-4 py-2">〜670文字</td><td className="px-4 py-2">33円</td></tr>
        </tbody>
      </table>
    </div>
  );
}

function SectionQuickSend() {
  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-600">
        ダッシュボード画面の「クイック送信」フォームから個別にSMSを送信できます。
      </p>

      {/* Step 1 */}
      <div className="flex gap-3">
        <StepNumber n={1} />
        <div className="flex-1 space-y-2">
          <p className="text-sm font-medium">宛先を入力</p>
          <p className="text-sm text-slate-500">
            電話番号を直接入力するか、顧客検索（会社名・名前・電話番号）で選択します。
            顧客を選択すると会社名・宛先名が自動入力されます。
          </p>
          <div className="bg-slate-50 p-3 rounded-lg border border-dashed border-slate-200 flex flex-wrap gap-3">
            <MockInput label="顧客検索" placeholder="会社名・名前・電話番号" width="w-52" />
            <MockInput label="宛先" placeholder="08012345678" width="w-40" />
            <MockInput label="会社名" placeholder="任意" width="w-32" />
            <MockInput label="宛先名" placeholder="お客様名" width="w-28" />
          </div>
        </div>
      </div>

      {/* Step 2 */}
      <div className="flex gap-3">
        <StepNumber n={2} />
        <div className="flex-1 space-y-2">
          <p className="text-sm font-medium">担当者・テンプレートを選択</p>
          <p className="text-sm text-slate-500">
            ログインユーザーが自動選択されます。テンプレートを選ぶと本文が自動展開され、
            予約ページリンク（<code className="text-xs bg-slate-100 px-1 rounded">{"{{booking_url}}"}</code>）も自動生成されます。
          </p>
          <div className="bg-slate-50 p-3 rounded-lg border border-dashed border-slate-200 flex gap-3">
            <MockInput label="担当者" placeholder="山田太郎" width="w-36" />
            <MockInput label="テンプレート" placeholder="follow_up" width="w-44" />
          </div>
        </div>
      </div>

      {/* Step 3 */}
      <div className="flex gap-3">
        <StepNumber n={3} />
        <div className="flex-1 space-y-2">
          <p className="text-sm font-medium">本文を入力して送信</p>
          <p className="text-sm text-slate-500">
            自由入力も可能です。先頭に <strong>【スフィアライト】</strong> が自動付与されます（入力不要）。
          </p>
          <div className="bg-slate-50 p-3 rounded-lg border border-dashed border-slate-200 space-y-2">
            <div className="w-full px-3 py-2 border border-dashed border-slate-300 rounded-md text-sm text-slate-400 bg-white h-16">
              メッセージを入力...
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">
                <Keyboard size={12} className="inline mr-1" />
                <Kbd>Cmd</Kbd> + <Kbd>Enter</Kbd> でも送信できます
              </span>
              <MockButton>送信</MockButton>
            </div>
          </div>
        </div>
      </div>

      {/* 文字数 */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
        <p className="text-sm font-medium text-blue-800 flex items-center gap-2">
          <AlertCircle size={16} /> 文字数について
        </p>
        <SegmentTable />
        <ul className="text-xs text-blue-700 space-y-1">
          <li>「【スフィアライト】」（8文字）を含んだ文字数です</li>
          <li>入力中にリアルタイムで文字数・セグメント数が表示されます</li>
          <li>670文字を超えると送信できません</li>
        </ul>
      </div>
    </div>
  );
}

function SectionBulkSend() {
  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-600">
        「一斉送信」メニューから、CSVファイルを使って複数の宛先へまとめてSMSを送信できます。
      </p>

      <div className="flex gap-3">
        <StepNumber n={1} />
        <div className="flex-1 space-y-2">
          <p className="text-sm font-medium">CSVファイルをアップロード</p>
          <p className="text-sm text-slate-500">
            CSVには <code className="text-xs bg-slate-100 px-1 rounded">phone</code>、<code className="text-xs bg-slate-100 px-1 rounded">tel</code>、または <code className="text-xs bg-slate-100 px-1 rounded">電話番号</code> という列名が必要です。
          </p>
          <div className="bg-slate-50 p-4 rounded-lg border-2 border-dashed border-slate-300 text-center">
            <Upload size={24} className="mx-auto text-slate-400 mb-2" />
            <p className="text-sm text-slate-500">CSVファイルをドラッグ&ドロップまたはクリックで選択</p>
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <StepNumber n={2} />
        <div className="flex-1 space-y-2">
          <p className="text-sm font-medium">本文を入力</p>
          <p className="text-sm text-slate-500">
            テンプレートまたは自由入力。CSV内の列名を変数として使えます。
          </p>
          <div className="bg-slate-50 p-3 rounded-lg border border-dashed border-slate-200">
            <div className="text-sm text-slate-600 bg-white border rounded-md px-3 py-2">
              <span className="text-blue-600">{"{{会社名}}"}</span>の<span className="text-blue-600">{"{{担当者名}}"}</span>様、お世話になっております。
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <StepNumber n={3} />
        <div className="flex-1 space-y-2">
          <p className="text-sm font-medium">確認して送信</p>
          <p className="text-sm text-slate-500">
            送信先件数・推定文字数・推定コストを確認して送信します。
            配信停止リストに登録されている番号は自動的にスキップされます。
          </p>
          <div className="bg-slate-50 p-3 rounded-lg border border-dashed border-slate-200 flex gap-4">
            <div className="text-center">
              <p className="text-lg font-bold text-slate-700">150</p>
              <p className="text-xs text-slate-400">送信先</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-slate-700">2通</p>
              <p className="text-xs text-slate-400">セグメント</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-blue-600">990円</p>
              <p className="text-xs text-slate-400">推定コスト</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm font-medium text-amber-800 mb-2">CSVファイルの例</p>
        <pre className="text-xs bg-white p-3 rounded border border-amber-200 font-mono overflow-x-auto">
{`phone,会社名,担当者名
09012345678,○○商事,山田太郎
08087654321,△△工業,佐藤花子`}
        </pre>
      </div>
    </div>
  );
}

function SectionConversations() {
  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-600">
        「会話」メニューから、顧客との送受信履歴をチャット形式で確認・返信できます。
      </p>

      <div className="bg-slate-50 rounded-lg border border-dashed border-slate-200 overflow-hidden">
        <div className="flex h-48">
          {/* Left panel mock */}
          <div className="w-48 border-r border-slate-200 bg-white">
            <div className="p-2 border-b border-slate-100">
              <div className="flex items-center gap-1.5 px-2 py-1.5 bg-slate-50 rounded text-xs text-slate-400">
                <Search size={12} /> 検索...
              </div>
            </div>
            <div className="p-1 space-y-0.5">
              <div className="px-2 py-1.5 bg-blue-50 rounded text-xs">
                <p className="font-medium text-slate-700">090-1234-5678</p>
                <p className="text-slate-400 truncate">ご予約ありがとう...</p>
              </div>
              <div className="px-2 py-1.5 rounded text-xs">
                <p className="font-medium text-slate-700">080-8765-4321</p>
                <p className="text-slate-400 truncate">お世話になっており...</p>
              </div>
            </div>
          </div>
          {/* Right panel mock */}
          <div className="flex-1 flex flex-col bg-white">
            <div className="flex-1 p-3 space-y-2 overflow-hidden">
              <div className="flex justify-end">
                <div className="bg-blue-500 text-white text-xs px-3 py-1.5 rounded-lg max-w-[70%]">
                  お世話になっております。ご予約はこちらから...
                  <span className="block text-right text-blue-200 text-[10px] mt-0.5">14:30 ✓✓</span>
                </div>
              </div>
              <div className="flex justify-start">
                <div className="bg-slate-100 text-xs px-3 py-1.5 rounded-lg max-w-[70%]">
                  承知しました。予約しました。
                  <span className="block text-right text-slate-400 text-[10px] mt-0.5">14:45</span>
                </div>
              </div>
            </div>
            <div className="p-2 border-t border-slate-100 flex gap-2">
              <div className="flex-1 px-2 py-1 border border-dashed border-slate-300 rounded text-xs text-slate-400 bg-slate-50">
                返信を入力...
              </div>
              <span className="px-2 py-1 bg-blue-500 text-white text-xs rounded">送信</span>
            </div>
          </div>
        </div>
      </div>

      <ul className="text-sm text-slate-600 space-y-2">
        <li className="flex items-start gap-2">
          <span className="text-slate-400 mt-0.5">&#8226;</span>
          左側に連絡先一覧（最新メッセージ順）、右側に会話履歴を表示
        </li>
        <li className="flex items-start gap-2">
          <span className="text-slate-400 mt-0.5">&#8226;</span>
          テキスト入力欄からそのまま返信が可能
        </li>
        <li className="flex items-start gap-2">
          <span className="text-slate-400 mt-0.5">&#8226;</span>
          受信メッセージは10秒ごとに自動更新
        </li>
        <li className="flex items-start gap-2">
          <span className="text-slate-400 mt-0.5">&#8226;</span>
          配信済み <span className="font-mono text-green-600">✓✓</span>　失敗 <span className="font-mono text-red-500">✕</span>
        </li>
      </ul>
    </div>
  );
}

function SectionTemplates() {
  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-600">
        「テンプレート」メニューで送信テンプレートの作成・編集ができます。
      </p>

      <div>
        <p className="text-sm font-medium mb-3">使用できる変数</p>
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">変数</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">内容</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">使用例</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <tr>
                <td className="px-4 py-2"><code className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{"{{staff_name_last}}"}</code></td>
                <td className="px-4 py-2 text-slate-600">担当者名（姓）</td>
                <td className="px-4 py-2 text-slate-500">担当の山田です</td>
              </tr>
              <tr className="bg-slate-50">
                <td className="px-4 py-2"><code className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{"{{company_name}}"}</code></td>
                <td className="px-4 py-2 text-slate-600">顧客の会社名</td>
                <td className="px-4 py-2 text-slate-500">○○商事様</td>
              </tr>
              <tr>
                <td className="px-4 py-2"><code className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{"{{contact_name}}"}</code></td>
                <td className="px-4 py-2 text-slate-600">顧客の名前</td>
                <td className="px-4 py-2 text-slate-500">佐藤様</td>
              </tr>
              <tr className="bg-slate-50">
                <td className="px-4 py-2"><code className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{"{{booking_url}}"}</code></td>
                <td className="px-4 py-2 text-slate-600">予約ページURL</td>
                <td className="px-4 py-2 text-slate-500">ご予約はこちら：https://...</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-1">
        <p className="text-xs text-blue-700">
          <code className="bg-blue-100 px-1 rounded">{"{{booking_url}}"}</code> は送信時に担当者を選択すると自動生成されます（有効期限7日）
        </p>
        <p className="text-xs text-blue-700">変数は本文中で色付き表示されます</p>
      </div>

      <div>
        <p className="text-sm font-medium mb-3">テンプレート作成手順</p>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <StepNumber n={1} />
            <p className="text-sm text-slate-600 pt-0.5">「新規作成」ボタンをクリック</p>
          </div>
          <div className="flex items-start gap-3">
            <StepNumber n={2} />
            <p className="text-sm text-slate-600 pt-0.5">
              Slug（識別名）を入力（例：<code className="text-xs bg-slate-100 px-1 rounded">follow_up</code>）
            </p>
          </div>
          <div className="flex items-start gap-3">
            <StepNumber n={3} />
            <p className="text-sm text-slate-600 pt-0.5">カテゴリを選択（Transactional / Marketing）</p>
          </div>
          <div className="flex items-start gap-3">
            <StepNumber n={4} />
            <p className="text-sm text-slate-600 pt-0.5">本文を入力（変数使用可）</p>
          </div>
          <div className="flex items-start gap-3">
            <StepNumber n={5} />
            <p className="text-sm text-slate-600 pt-0.5">文字数を確認して「保存」</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionMessages() {
  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-600">
        「メッセージ」メニューで全ての送受信履歴を確認できます。
      </p>

      <div className="bg-slate-50 p-3 rounded-lg border border-dashed border-slate-200 space-y-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border rounded text-xs text-slate-400 w-48">
            <Search size={12} /> 電話番号や本文で検索
          </div>
          <div className="flex gap-1">
            <MockBadge color="slate">ALL</MockBadge>
            <MockBadge color="yellow">QUEUED</MockBadge>
            <MockBadge color="blue">SENDING</MockBadge>
            <MockBadge color="green">DELIVERED</MockBadge>
            <MockBadge color="red">FAILED</MockBadge>
            <MockBadge color="slate">RECEIVED</MockBadge>
          </div>
        </div>
      </div>

      <div>
        <p className="text-sm font-medium mb-3">ステータスの見方</p>
        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center gap-2 p-2 bg-yellow-50 rounded border border-yellow-200">
            <MockBadge color="yellow">QUEUED</MockBadge>
            <span className="text-xs text-slate-600">送信待ち</span>
          </div>
          <div className="flex items-center gap-2 p-2 bg-blue-50 rounded border border-blue-200">
            <MockBadge color="blue">SENDING</MockBadge>
            <span className="text-xs text-slate-600">送信中</span>
          </div>
          <div className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
            <MockBadge color="green">DELIVERED</MockBadge>
            <span className="text-xs text-slate-600">配信済み</span>
          </div>
          <div className="flex items-center gap-2 p-2 bg-red-50 rounded border border-red-200">
            <MockBadge color="red">FAILED</MockBadge>
            <span className="text-xs text-slate-600">送信失敗</span>
          </div>
          <div className="flex items-center gap-2 p-2 bg-slate-50 rounded border border-slate-200">
            <MockBadge color="slate">RECEIVED</MockBadge>
            <span className="text-xs text-slate-600">受信</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionOptOuts() {
  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-600">
        「配信停止」メニューで、SMS送信を停止する電話番号を管理します。
      </p>

      <div className="bg-slate-50 p-4 rounded-lg border border-dashed border-slate-200 space-y-3">
        <div className="flex items-center gap-2">
          <div className="px-3 py-2 border border-dashed border-slate-300 bg-white rounded-md text-sm text-slate-400 w-48">
            電話番号を入力
          </div>
          <MockButton color="red">配信停止に追加</MockButton>
        </div>
        <div className="border border-slate-200 rounded bg-white divide-y">
          <div className="flex items-center justify-between px-3 py-2 text-sm">
            <span className="text-slate-700">090-1234-5678</span>
            <MockButton color="slate">解除</MockButton>
          </div>
          <div className="flex items-center justify-between px-3 py-2 text-sm">
            <span className="text-slate-700">080-8765-4321</span>
            <MockButton color="slate">解除</MockButton>
          </div>
        </div>
      </div>

      <ul className="text-sm text-slate-600 space-y-2">
        <li className="flex items-start gap-2">
          <span className="text-slate-400 mt-0.5">&#8226;</span>
          配信停止中の番号にSMSを送信しようとするとエラーになります
        </li>
        <li className="flex items-start gap-2">
          <span className="text-slate-400 mt-0.5">&#8226;</span>
          一斉送信時は自動的にスキップされます
        </li>
        <li className="flex items-start gap-2">
          <span className="text-slate-400 mt-0.5">&#8226;</span>
          「解除」ボタンで確認後に配信停止を解除できます
        </li>
      </ul>
    </div>
  );
}

function SectionDevices() {
  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-600">
        「端末」メニューでSMS送信に使用するAndroid端末の状態を確認できます。
      </p>

      <div className="bg-slate-50 p-4 rounded-lg border border-dashed border-slate-200">
        <div className="flex items-center gap-4 p-3 bg-white rounded-lg border">
          <Smartphone size={32} className="text-slate-400" />
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-700">Device #1</p>
            <p className="text-xs text-slate-400">最終応答: 2分前</p>
          </div>
          <MockBadge color="green">ONLINE</MockBadge>
          <div className="text-right">
            <p className="text-lg font-bold text-slate-700">42</p>
            <p className="text-xs text-slate-400">本日送信</p>
          </div>
        </div>
      </div>

      <div>
        <p className="text-sm font-medium mb-3">ステータスの見方</p>
        <div className="space-y-2">
          <div className="flex items-center gap-3 p-2 bg-green-50 rounded border border-green-200">
            <MockBadge color="green">ONLINE</MockBadge>
            <span className="text-sm text-slate-600">正常に稼働中</span>
          </div>
          <div className="flex items-center gap-3 p-2 bg-slate-50 rounded border border-slate-200">
            <MockBadge color="slate">OFFLINE</MockBadge>
            <span className="text-sm text-slate-600">端末が応答なし（SMSはキューに溜まり、復帰後に送信）</span>
          </div>
          <div className="flex items-center gap-3 p-2 bg-red-50 rounded border border-red-200">
            <MockBadge color="red">ERROR</MockBadge>
            <span className="text-sm text-slate-600">エラー発生中</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionSettings() {
  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-600">
        「設定」メニューで送信時間帯の制限を管理します（管理者のみ）。
      </p>

      <div className="bg-slate-50 p-4 rounded-lg border border-dashed border-slate-200 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-700">SMS送信時間帯制限</p>
            <p className="text-xs text-slate-400 mt-0.5">ONの場合、時間帯外のSMSは翌営業時間開始まで自動的に遅延されます</p>
          </div>
          <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-blue-500">
            <span className="inline-block h-4 w-4 transform rounded-full bg-white translate-x-6" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div>
            <span className="block text-xs text-slate-500 mb-1">開始 (JST)</span>
            <div className="px-3 py-1.5 border border-dashed border-slate-300 bg-white rounded text-sm w-20 text-center">
              09:00
            </div>
          </div>
          <span className="text-slate-400 mt-4">〜</span>
          <div>
            <span className="block text-xs text-slate-500 mb-1">終了 (JST)</span>
            <div className="px-3 py-1.5 border border-dashed border-slate-300 bg-white rounded text-sm w-20 text-center">
              20:00
            </div>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <Clock size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-blue-700 space-y-1">
            <p><strong>ON:</strong> 設定した時間帯（デフォルト 9:00〜20:00）の外ではSMSが自動遅延されます</p>
            <p><strong>OFF:</strong> 24時間いつでも即座に送信されます</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionDashboard() {
  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-600">
        ダッシュボード上部に本日の送信統計が表示されます。
      </p>

      <div className="bg-slate-50 p-3 rounded-lg border border-dashed border-slate-200">
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-white p-3 rounded-lg border text-center">
            <p className="text-xs text-slate-500">本日送信</p>
            <p className="text-xl font-bold mt-1">42</p>
          </div>
          <div className="bg-white p-3 rounded-lg border text-center">
            <p className="text-xs text-slate-500">成功率</p>
            <p className="text-xl font-bold mt-1">95%</p>
          </div>
          <div className="bg-white p-3 rounded-lg border text-center">
            <p className="text-xs text-slate-500">失敗</p>
            <p className="text-xl font-bold mt-1">2</p>
          </div>
          <div className="bg-white p-3 rounded-lg border text-center">
            <p className="text-xs text-slate-500">コスト</p>
            <p className="text-xl font-bold mt-1">277円</p>
          </div>
        </div>
      </div>

      <ul className="text-sm text-slate-600 space-y-2">
        <li className="flex items-start gap-2">
          <span className="text-slate-400 mt-0.5">&#8226;</span>
          過去30日間の日別推移グラフも表示されます
        </li>
        <li className="flex items-start gap-2">
          <span className="text-slate-400 mt-0.5">&#8226;</span>
          クイック送信フォームもダッシュボードに配置されています
        </li>
      </ul>
    </div>
  );
}

function SectionFAQ() {
  const faqs = [
    {
      q: "送信したのにステータスが「QUEUED」のまま動かない",
      a: "送信時間帯制限がONの場合、営業時間外のSMSは翌営業時間まで自動遅延されます。設定画面で確認してください。端末がOFFLINEの場合もキューに溜まります。",
    },
    {
      q: "「Recipient has opted out」と表示される",
      a: "宛先が配信停止リストに登録されています。「配信停止」メニューで確認・解除できます。",
    },
    {
      q: "テンプレートの変数が展開されない",
      a: "{{変数名}} の形式で入力してください。担当者を選択しないと {{booking_url}} は生成されません。",
    },
    {
      q: "文字数オーバーで送信できない",
      a: "「【スフィアライト】」（8文字）を含めて670文字以内にしてください。テンプレートの変数は展開後の推定文字数で計算されます。",
    },
  ];

  return (
    <div className="space-y-3">
      {faqs.map((faq, i) => (
        <div key={i} className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="bg-slate-50 px-4 py-3">
            <p className="text-sm font-medium text-slate-700">Q: {faq.q}</p>
          </div>
          <div className="px-4 py-3">
            <p className="text-sm text-slate-600">A: {faq.a}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

const sectionComponents: Record<SectionId, FC> = {
  "quick-send": SectionQuickSend,
  "bulk-send": SectionBulkSend,
  conversations: SectionConversations,
  templates: SectionTemplates,
  messages: SectionMessages,
  "opt-outs": SectionOptOuts,
  devices: SectionDevices,
  settings: SectionSettings,
  dashboard: SectionDashboard,
  faq: SectionFAQ,
};

export default function Help() {
  // URLハッシュから初期セクションを決定（例: /help#bulk-send）
  const initialSection = (window.location.hash.replace("#", "") || "dashboard") as SectionId;
  const [openSection, setOpenSection] = useState<SectionId | null>(
    sections.some((s) => s.id === initialSection) ? initialSection : "dashboard",
  );

  const toggle = (id: SectionId) => {
    setOpenSection((prev) => (prev === id ? null : id));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold">ヘルプ</h2>
        <p className="text-sm text-slate-500 mt-1">SMS管理システムの使い方ガイド</p>
      </div>

      <div className="max-w-3xl space-y-2">
        {sections.map(({ id, icon: Icon, title }) => {
          const isOpen = openSection === id;
          const Content = sectionComponents[id];
          return (
            <div key={id} className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <button
                onClick={() => toggle(id)}
                className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
              >
                <Icon size={18} className="text-slate-500 flex-shrink-0" />
                <span className="flex-1 text-sm font-medium text-slate-700">{title}</span>
                {isOpen ? (
                  <ChevronDown size={16} className="text-slate-400" />
                ) : (
                  <ChevronRight size={16} className="text-slate-400" />
                )}
              </button>
              {isOpen && (
                <div className="px-5 pb-5 pt-1 border-t border-slate-100">
                  <Content />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
